import React, { useEffect, useMemo, useState } from 'react';
import { useAppContext } from 'AppContext';
import State from 'utils/State';

import Title from 'components/Title/Title';
import { List } from 'components/Menu';
import { Tiles } from 'components/Tiles/Tiles';
import { Link } from 'components/Link/Link';
import { getAllMenuItems, getAllMenuVariants } from 'utils/Menu';
import { App } from 'generated/graphql';
import { listAppsInString } from 'utils';
import { AmplitudeEventCategories, AmplitudeEventProperties, logAmplitudeEvent } from 'utils/Amplitude';
import useFeatureFlagsQuery from 'common/Hooks/UseFeatureFlagsQuery';
import { FeatureTiles } from 'components/FeatureTiles/FeatureTiles';
import { useVenueContext } from './Main';
import { venueHasBrands } from '../utils/brand';

import styles from './Menu.module.scss';

const { headerCustomTheme } = State;

const AVAILABILITY_BY_VARIANT_FEATURE_FLAG = 'availabilityByVariantEnabled';

const Menu: React.FC = () => {
  const [view, setView] = useState<string>('items');
  const { currentVenue, isLoading } = useVenueContext();
  const [data, loading, error] = useFeatureFlagsQuery([AVAILABILITY_BY_VARIANT_FEATURE_FLAG]);

  const { draftMenus, setDraftMenus } = useAppContext();

  useEffect(() => {
    if (draftMenus.length) {
      setDraftMenus([]);
    }
  }, [draftMenus, setDraftMenus]);

  const { isAvailabilityByVariantEnabled } = useMemo(() => {
    if (loading || error || !data) {
      return {
        isAvailabilityByVariantEnabled: false,
        isSurchargeEnabled: false
      };
    }

    const availabilityByVariantFeatureFlag = data[AVAILABILITY_BY_VARIANT_FEATURE_FLAG];
    return {
      isAvailabilityByVariantEnabled: availabilityByVariantFeatureFlag
        ? availabilityByVariantFeatureFlag.isEnabled
        : false
    };
  }, [loading, error, data]);

  useEffect(() => {
    headerCustomTheme(null);
  }, []);

  useEffect(() => {
    if (currentVenue?.hashedId || currentVenue?.posName) {
      logAmplitudeEvent({
        name: view === 'items' ? 'view all items' : 'view options',
        category: view === 'items' ? AmplitudeEventCategories.MM_ALL_ITEMS : AmplitudeEventCategories.MM_OPTIONS,
        properties: {
          [AmplitudeEventProperties.VENUE_ID]: currentVenue.hashedId,
          [AmplitudeEventProperties.POS_NAME]: currentVenue.posName || ''
        }
      });
    }
  }, [currentVenue?.hashedId, currentVenue?.posName, view]);

  const uniqueApps = useMemo((): App[] => {
    const response: App[] = [];
    currentVenue?.menus?.forEach((menu) => {
      menu.apps.forEach((app) => {
        const appInResponse = response.find((item) => item.name === app.name);

        if (!appInResponse) {
          response.push(app);
        }
      });
    });

    return response;
  }, [currentVenue]);

  const hasBrands = useMemo(() => venueHasBrands(currentVenue?.menus ?? []), [currentVenue?.menus]);

  const onSeeOtherSupportedAppsClick = (): void => {
    logAmplitudeEvent({
      name: 'click see other supported apps',
      category: AmplitudeEventCategories.MM_ALL_ITEMS
    });
  };

  return (
    <>
      <Title
        title="Manage your menus"
        subTitle={
          <>
            {/* We want to show unique names of all apps with menus regardless of brands */}
            You can now view and update your {listAppsInString(uniqueApps, false)} menus.{' '}
            <Link onClick={onSeeOtherSupportedAppsClick} href="about">
              See other supported apps and more
            </Link>
          </>
        }
      />
      <FeatureTiles currentVenue={currentVenue} />
      <Tiles
        isLoading={isLoading}
        menus={currentVenue?.menus}
        venueName={currentVenue?.name}
        timezone={currentVenue?.timezone}
      />
      <section aria-labelledby="all-items-heading" className={`${styles.Outer} container`}>
        <div className={styles.Header}>
          <div>
            <h2 id="all-items-heading">All items</h2>
            <p>Includes all your published items.</p>
          </div>
        </div>
        <ul className={styles.Tabs}>
          <li className={view === 'items' ? styles.Active : ''}>
            <button onClick={() => setView('items')} data-testid="details-items-button" type="button">
              Items
            </button>
          </li>
          {isAvailabilityByVariantEnabled && (
            <li className={view === 'options' ? styles.Active : ''}>
              <button onClick={() => setView('options')} data-testid="details-options-button" type="button">
                Options
              </button>
            </li>
          )}
        </ul>
        {view === 'items' ? (
          <List
            isLoading={isLoading}
            listItems={getAllMenuItems(currentVenue)}
            venue={currentVenue}
            hasBrands={hasBrands}
          />
        ) : (
          <List
            isLoading={isLoading}
            listItems={getAllMenuVariants(currentVenue)}
            venue={currentVenue}
            hasBrands={hasBrands}
          />
        )}
      </section>
    </>
  );
};

export default Menu;
